import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { API_BASE, getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtMoney } from "@/lib/pricing";
import { BadgeCard } from "@/components/BadgeCard";
import { RankChip } from "@/components/RankChip";
import { BackButton } from "@/components/BackButton";
import {
  Crosshair,
  Flame,
  Skull,
  Volume2,
  VolumeX,
  Upload,
  Trash2,
  Play,
  Trophy,
  Zap,
  Phone,
  TrendingUp,
  Award,
  RotateCcw,
} from "lucide-react";

const PERIODS = [
  { key: "day", label: "Today" },
  { key: "week", label: "This week" },
  { key: "month", label: "This month" },
];

export default function Arena() {
  const { user } = useAuth();
  const [period, setPeriod] = useState("month");
  const [leaderboard, setLeaderboard] = useState(null);
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [lb, m] = await Promise.all([
        api.get("/arena/leaderboard", { params: { period } }),
        api.get("/arena/me"),
      ]);
      setLeaderboard(lb.data);
      setMe(m.data);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      if (!cancelled) await load();
    })();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const canParticipate = user?.role === "rep";

  return (
    <div className="flex-1 overflow-y-auto arena-root" data-testid="arena-page">
      {/* Ambient background */}
      <div className="arena-bg" />

      <div className="relative px-8 py-6 max-w-6xl">
        <Header period={period} setPeriod={setPeriod} me={me} />

        {loading ? (
          <div className="mt-8 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-32 border border-line rounded-sm bg-bg-surface animate-pulse" />
            ))}
          </div>
        ) : (
          <>
            <Podium rows={(leaderboard?.rows || []).slice(0, 3)} />
            <div className="mt-8 grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <LeaderboardTable rows={leaderboard?.rows || []} meId={user?.id} />
                <RecentKillsFeed />
              </div>
              <div className="space-y-6">
                <LoadoutCard me={me} canParticipate={canParticipate} onSaved={load} />
                <BadgesGrid badges={me?.badges || []} />
              </div>
            </div>
          </>
        )}
      </div>

      <style>{`
        .arena-root { position: relative; }
        .arena-bg {
          position: fixed; inset: 0; pointer-events: none; z-index: 0;
          background:
            radial-gradient(circle at 20% 10%, rgba(255, 106, 0, 0.06) 0%, transparent 45%),
            radial-gradient(circle at 80% 90%, rgba(34, 211, 192, 0.05) 0%, transparent 50%),
            linear-gradient(180deg, #08090B 0%, #0E1315 100%);
        }
        .arena-root::before {
          content: "";
          position: fixed; inset: 0;
          background-image:
            linear-gradient(rgba(255, 255, 255, 0.02) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255, 255, 255, 0.02) 1px, transparent 1px);
          background-size: 40px 40px;
          pointer-events: none;
          z-index: 0;
        }
      `}</style>
    </div>
  );
}

function Header({ period, setPeriod, me }) {
  return (
    <div className="relative">
      <div className="mb-3">
        <BackButton />
      </div>
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center w-10 h-10"
          style={{
            background: "linear-gradient(135deg, #FF6A00 0%, #FF2A00 100%)",
            clipPath: "polygon(6px 0, 100% 0, calc(100% - 6px) 100%, 0 100%)",
            boxShadow: "0 0 20px rgba(255, 106, 0, 0.5)",
          }}
        >
          <Crosshair size={20} className="text-white" />
        </div>
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.3em] text-orange-400" style={{ color: "#FF6A00" }}>
            Compete
          </div>
          <h1 className="text-3xl font-bold tracking-tight text-ink">Arena</h1>
        </div>
      </div>
      <div className="mt-2 flex items-baseline justify-between gap-4 flex-wrap">
        <p className="text-sm text-ink-secondary max-w-lg">
          Live scoreboard for closes. Setup + 12×MRR ranks the board. Play hard —
          your walk-up track fires the moment a deal closes.
        </p>
        <div className="flex items-center gap-3">
          {me?.rank_label && (
            <RankChip rankLabel={me.rank_label} rank={me.rank} />
          )}
          <div className="flex border border-line rounded-sm overflow-hidden" data-testid="arena-period-toggle">
            {PERIODS.map((p) => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`arena-period-${p.key}`}
                className={`text-xs px-3 py-1.5 font-mono uppercase tracking-wider transition-colors ${
                  period === p.key
                    ? "bg-cyan text-bg"
                    : "bg-bg-surface text-ink-secondary hover:text-ink hover:bg-bg-hover"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Podium({ rows }) {
  if (rows.length === 0) {
    return (
      <div className="mt-8 border border-line rounded-sm bg-bg-surface p-8 text-center" data-testid="arena-podium-empty">
        <Trophy size={28} className="mx-auto text-ink-muted" />
        <div className="mt-3 text-sm text-ink-secondary">
          No closes yet in this period. Be the first on the board.
        </div>
      </div>
    );
  }

  const [first, second, third] = [rows[0], rows[1], rows[2]];

  return (
    <div className="mt-6 grid grid-cols-3 gap-3" data-testid="arena-podium">
      {second && <PodiumCard row={second} place={2} />}
      {first && <PodiumCard row={first} place={1} />}
      {third && <PodiumCard row={third} place={3} />}
    </div>
  );
}

function PodiumCard({ row, place }) {
  const meta = {
    1: {
      ring: "#FFD873",
      glow: "rgba(255, 195, 60, 0.6)",
      label: "CHAMPION",
      height: "h-56",
    },
    2: {
      ring: "#DFE9EF",
      glow: "rgba(200, 220, 230, 0.5)",
      label: "RUNNER UP",
      height: "h-48",
    },
    3: {
      ring: "#E7B57F",
      glow: "rgba(180, 120, 60, 0.5)",
      label: "3RD",
      height: "h-44",
    },
  }[place];

  return (
    <div
      className={`relative ${meta.height} overflow-hidden`}
      style={{
        background: `linear-gradient(180deg, ${meta.glow} 0%, rgba(14,19,21,0.98) 60%, rgba(14,19,21,1) 100%)`,
        border: `1px solid ${meta.ring}80`,
        boxShadow: `0 0 30px -8px ${meta.glow}`,
        clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 12px), calc(100% - 12px) 100%, 0 100%)",
      }}
      data-testid={`arena-podium-${place}`}
    >
      <div
        className="absolute top-2 left-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5"
        style={{
          color: meta.ring,
          border: `1px solid ${meta.ring}80`,
          background: "rgba(0,0,0,0.4)",
        }}
      >
        {meta.label}
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-end p-5">
        <div
          className="text-6xl font-black tabular-nums leading-none"
          style={{
            color: meta.ring,
            textShadow: `0 0 24px ${meta.glow}`,
            fontFamily: "JetBrains Mono, monospace",
          }}
        >
          #{place}
        </div>
        <div className="mt-2 text-lg font-bold text-ink truncate max-w-full text-center">
          {row.name}
        </div>
        <div className="mt-1 text-2xl font-bold tabular-nums text-ink" data-testid={`arena-podium-${place}-score`}>
          {fmtMoney(row.score_cents)}
        </div>
        <div className="mt-0.5 text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          {row.closes} close{row.closes === 1 ? "" : "s"} · {row.setup_cents > 0 ? fmtMoney(row.setup_cents) + " setup" : "no setup"} · {row.mrr_cents > 0 ? fmtMoney(row.mrr_cents) + "/mo" : "no MRR"}
        </div>
      </div>
    </div>
  );
}

function LeaderboardTable({ rows, meId }) {
  return (
    <section
      className="border border-line rounded-sm bg-bg-surface overflow-hidden"
      data-testid="arena-leaderboard-table"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Trophy size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">Full board</h2>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          Sorted by ACV (setup + 12×MRR)
        </span>
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-ink-muted">
          No reps active yet.
        </div>
      ) : (
        <div className="divide-y divide-line">
          {rows.map((r) => {
            const isMe = r.user_id === meId;
            return (
              <div
                key={r.user_id}
                className={`px-5 py-3 flex items-center gap-4 transition-colors ${
                  isMe ? "bg-cyan-muted" : ""
                }`}
                data-testid={`arena-row-${r.user_id}`}
              >
                <div
                  className="text-lg font-mono font-bold tabular-nums w-10 text-center"
                  style={{ color: r.rank === 1 ? "#FFD873" : r.rank <= 3 ? "#8AECFF" : "#70827D" }}
                >
                  #{r.rank}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-ink truncate">{r.name}</span>
                    <RankChip rankLabel={r.rank_label} rank={r.rank} size="sm" />
                    {isMe && (
                      <span className="text-[9px] font-mono uppercase tracking-widest text-cyan">
                        YOU
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-[11px] text-ink-muted font-mono">
                    <span data-testid={`row-${r.user_id}-closes`}>
                      <Trophy size={10} className="inline mr-1" />
                      {r.closes} close{r.closes === 1 ? "" : "s"}
                    </span>
                    <span data-testid={`row-${r.user_id}-dials`}>
                      <Phone size={10} className="inline mr-1" />
                      {r.dials} dial{r.dials === 1 ? "" : "s"}
                    </span>
                    <span>
                      {r.connects} connect{r.connects === 1 ? "" : "s"}
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div
                    className="text-lg font-bold tabular-nums"
                    style={{ color: r.rank === 1 ? "#FFD873" : "#EFF4F2" }}
                  >
                    {fmtMoney(r.score_cents)}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
                    ACV score
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}

function RecentKillsFeed() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/arena/feed", { params: { limit: 15 } });
        if (!cancelled) {
          setEvents((data?.events || []).slice().reverse());
        }
      } catch (e) {
        // silent
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <section
      className="border border-line rounded-sm bg-bg-surface"
      data-testid="arena-recent-kills"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Flame size={14} style={{ color: "#FF6A00" }} />
        <h2 className="text-base font-medium text-ink">Recent kills</h2>
      </div>
      {loading ? (
        <div className="px-5 py-4 space-y-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-8 bg-bg animate-pulse rounded-sm" />
          ))}
        </div>
      ) : events.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-ink-muted">
          No closes logged yet. When one lands here, you&rsquo;ll hear the walk-up.
        </div>
      ) : (
        <div className="divide-y divide-line">
          {events.map((ev) => (
            <div key={ev.id} className="px-5 py-2.5 flex items-center gap-3 text-sm" data-testid={`kill-${ev.id}`}>
              <Skull size={13} style={{ color: "#FF6A00" }} className="shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="font-medium text-ink">{ev.user_name || "Unattributed"}</span>
                <span className="text-ink-muted"> closed </span>
                <span className="text-ink truncate">{ev.lead_name}</span>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs tabular-nums text-ink font-mono">
                  {fmtMoney((ev.one_time_cents || 0))}
                </div>
                {ev.mrr_cents > 0 && (
                  <div className="text-[10px] tabular-nums text-cyan font-mono">
                    +{fmtMoney(ev.mrr_cents)}/mo
                  </div>
                )}
              </div>
              <div className="text-[10px] text-ink-muted font-mono shrink-0">
                {formatRelativeTime(ev.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function LoadoutCard({ me, canParticipate, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [dndSaving, setDndSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [target, setTarget] = useState(me?.dial_target_daily || 100);
  const [targetSaving, setTargetSaving] = useState(false);
  const audioRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    setTarget(me?.dial_target_daily || 100);
  }, [me?.dial_target_daily]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024) {
      toast.error("File too large. Max 3MB.");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("audio", file);
      const { data: resp } = await api.post("/arena/walkup-track", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (resp?.note) {
        toast.warning(resp.note, { duration: 8000 });
      } else {
        toast.success("Walk-up track saved. Trimmed to 7 seconds.");
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const remove = async () => {
    setUploading(true);
    try {
      await api.delete("/arena/walkup-track");
      toast.success("Walk-up track removed.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setUploading(false);
    }
  };

  const preview = async () => {
    if (!me?.has_walkup) return;
    setPreviewing(true);
    try {
      const resp = await fetch(`${API_BASE}/arena/walkup-track/${me.user_id}`, {
        credentials: "include",
        headers: (() => {
          const t = localStorage.getItem("msc_access_token");
          return t ? { Authorization: `Bearer ${t}` } : {};
        })(),
      });
      if (!resp.ok) throw new Error("Could not load track");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      if (audioRef.current) {
        audioRef.current.src = url;
        audioRef.current.volume = 0.85;
        await audioRef.current.play();
        audioRef.current.onended = () => {
          URL.revokeObjectURL(url);
          setPreviewing(false);
        };
      }
    } catch (e) {
      toast.error(e?.message || "Playback failed. Tap ARM Sound at the top-right of the app first.");
      setPreviewing(false);
    }
  };

  const toggleDnd = async () => {
    setDndSaving(true);
    try {
      const { data } = await api.post("/arena/dnd", { dnd: !me?.dnd });
      toast.success(data.dnd ? "Sound muted — visual takeovers still fire." : "Sound on — walk-up will play.");
      onSaved && onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setDndSaving(false);
    }
  };

  const saveTarget = async () => {
    setTargetSaving(true);
    try {
      await api.post("/arena/targets", { dials_daily: Number(target) });
      toast.success(`Daily dial target: ${target}`);
      onSaved && onSaved();
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    } finally {
      setTargetSaving(false);
    }
  };

  if (!me) return null;

  return (
    <section
      className="border rounded-sm overflow-hidden"
      style={{
        background: "linear-gradient(160deg, rgba(255, 106, 0, 0.08) 0%, rgba(14,19,21,0.95) 60%, rgba(14,19,21,1) 100%)",
        borderColor: "#FF6A0055",
      }}
      data-testid="arena-loadout"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Zap size={14} style={{ color: "#FF6A00" }} />
        <h2 className="text-base font-medium text-ink">Your loadout</h2>
      </div>

      <div className="px-5 py-4 space-y-4">
        {/* Rank + streak */}
        <div className="grid grid-cols-2 gap-3">
          <div className="border border-line rounded-sm bg-bg p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              Monthly rank
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums text-ink" data-testid="loadout-rank">
              {me.rank ? `#${me.rank}` : "—"}
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-1">
              {me.monthly_closes} close{me.monthly_closes === 1 ? "" : "s"}
            </div>
          </div>
          <div className="border border-line rounded-sm bg-bg p-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
              Dial streak
            </div>
            <div className="mt-1 text-2xl font-bold tabular-nums flex items-baseline gap-1" data-testid="loadout-streak">
              <span style={{ color: me.streak_days > 0 ? "#FF6A00" : "#70827D" }}>
                {me.streak_days}
              </span>
              <Flame size={16} style={{ color: me.streak_days > 0 ? "#FF6A00" : "#70827D" }} />
            </div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mt-1">
              weekday{me.streak_days === 1 ? "" : "s"}
            </div>
          </div>
        </div>

        {/* Walk-up track */}
        <div>
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-secondary flex items-center gap-1.5">
              <Volume2 size={11} />
              Walk-up track
              <span className="text-ink-muted">· 7-second cap</span>
            </div>
            <button
              onClick={toggleDnd}
              disabled={dndSaving}
              data-testid="loadout-dnd-toggle"
              className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 border transition-colors ${
                me.dnd
                  ? "text-star border-star bg-star/10"
                  : "text-cyan border-cyan bg-cyan-muted"
              }`}
            >
              {me.dnd ? (
                <><VolumeX size={10} className="inline mr-1" />DND ON</>
              ) : (
                <><Volume2 size={10} className="inline mr-1" />Sound ON</>
              )}
            </button>
          </div>
          {me.has_walkup ? (
            <div className="border border-line rounded-sm bg-bg p-3 flex items-center gap-3" data-testid="loadout-walkup-set">
              <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "rgba(255, 106, 0, 0.2)", border: "1px solid #FF6A00" }}>
                <Volume2 size={14} style={{ color: "#FF6A00" }} />
              </div>
              <div className="flex-1 text-sm text-ink">Walk-up set</div>
              <button
                onClick={preview}
                disabled={previewing}
                data-testid="loadout-preview-btn"
                className="flex items-center gap-1 text-xs border border-line hover:bg-bg-hover text-ink px-2 py-1 transition-colors disabled:opacity-40"
              >
                <Play size={10} /> {previewing ? "…" : "Preview"}
              </button>
              <button
                onClick={remove}
                disabled={uploading}
                data-testid="loadout-remove-btn"
                className="flex items-center gap-1 text-xs border border-line hover:bg-bg-hover text-ink-secondary px-2 py-1 transition-colors disabled:opacity-40"
              >
                <Trash2 size={10} />
              </button>
              <audio ref={audioRef} />
            </div>
          ) : (
            <div className="border border-dashed border-line rounded-sm bg-bg p-3 text-center" data-testid="loadout-walkup-empty">
              <div className="text-xs text-ink-muted">No walk-up set. Upload mp3/m4a.</div>
            </div>
          )}
          <div className="mt-2 flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept="audio/mpeg,audio/mp3,audio/mp4,audio/x-m4a,audio/m4a,audio/wav,audio/ogg"
              className="hidden"
              onChange={(e) => upload(e.target.files?.[0])}
              data-testid="loadout-upload-input"
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="loadout-upload-btn"
              className="flex-1 flex items-center justify-center gap-2 text-xs font-medium py-2 border transition-colors disabled:opacity-40"
              style={{
                background: "linear-gradient(90deg, #FF6A00 0%, #FF3A00 100%)",
                color: "white",
                borderColor: "#FF6A00",
                clipPath: "polygon(4px 0, 100% 0, calc(100% - 4px) 100%, 0 100%)",
              }}
            >
              <Upload size={12} />
              {uploading ? "Uploading…" : me.has_walkup ? "Replace track" : "Upload walk-up"}
            </button>
          </div>
        </div>

        {/* Daily dial target */}
        {canParticipate && (
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider text-ink-secondary mb-2 flex items-center gap-1.5">
              <Phone size={11} />
              Daily dial target
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                max="500"
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                data-testid="loadout-target-input"
                className="flex-1 bg-bg border border-line rounded-sm px-3 py-1.5 text-sm text-ink font-mono focus:border-cyan transition-colors tabular-nums"
              />
              <button
                onClick={saveTarget}
                disabled={targetSaving}
                data-testid="loadout-target-save"
                className="text-xs border border-line hover:bg-bg-hover text-ink px-3 py-1.5 transition-colors disabled:opacity-40"
              >
                {targetSaving ? "…" : "Save"}
              </button>
              <button
                onClick={() => setTarget(100)}
                data-testid="loadout-target-reset"
                className="text-xs text-ink-muted hover:text-ink border border-line hover:bg-bg-hover px-2 py-1.5 transition-colors"
                title="Reset to 100 (team baseline)"
              >
                <RotateCcw size={10} />
              </button>
            </div>
            <div className="mt-1.5 text-[10px] font-mono text-ink-muted">
              Base team target: 100 dials/weekday
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function BadgesGrid({ badges }) {
  const unlockedCount = badges.filter((b) => b.unlocked).length;
  return (
    <section
      className="border border-line rounded-sm bg-bg-surface"
      data-testid="arena-badges-grid"
    >
      <div className="px-5 py-3 border-b border-line flex items-center gap-2">
        <Award size={14} className="text-cyan" />
        <h2 className="text-base font-medium text-ink">Badges</h2>
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-ink-muted">
          {unlockedCount} / {badges.length}
        </span>
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        {badges.map((b) => (
          <BadgeCard key={b.key} badge={b} size="sm" />
        ))}
      </div>
    </section>
  );
}

function formatRelativeTime(iso) {
  if (!iso) return "";
  try {
    const now = Date.now();
    const t = new Date(iso).getTime();
    const s = Math.floor((now - t) / 1000);
    if (s < 60) return `${s}s ago`;
    if (s < 3600) return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
  } catch (e) {
    return "";
  }
}
